import { Injectable } from '@angular/core';
import { I18NextPipe } from 'angular-i18next';
import { TableColumn } from 'app/component-library/table/table-column';
import { TableConfig } from 'app/component-library/table/table-config';
import { TableParameters } from 'app/component-library/table/table-parameters';
import { BackendPaginationService } from 'app/core/backend-pagination.service';
import { BackendService } from 'app/core/backend.service';
import { CONFIG } from 'app/core/config';
import { ItemScenarios } from 'app/shared/enums/item-scenarios';
import { RunnableLookupCopyState } from 'app/shared/enums/runnable-lookup-copy-state';
import { GenerateGuidService } from 'app/shared/generate-guid/generate-guid.service';
import { LookupColumnDataType } from 'app/viewmodels/lookups/lookup-column-data-type';
import { LookupColumnRequest } from 'app/viewmodels/runnableLookups/lookup-column-request';
import { RunnableLookupBriefInfo } from 'app/viewmodels/runnableLookups/runnable-lookup-brief-info';
import { RunnableLookupItemState } from 'app/viewmodels/runnableLookups/runnable-lookup-item-state';
import { RunnableLookupSaveData } from 'app/viewmodels/runnableLookups/runnable-lookup-save-data';
import { RunnableLookupRow } from 'app/viewmodels/runnableLookups/runnableLookup-row';
import * as _ from 'lodash';
import { LazyLoadEvent } from 'primeng/api';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { finalize, map, take } from 'rxjs/operators';
import { RunnableLookupRowsRangeDeleteData } from '../../viewmodels/runnableLookups/runnable-lookup-rows-range-delete-data';
import { RunnableLookupColumn } from '../../viewmodels/runnableLookups/runnableLookup-column';
import { AdminLookupDataService } from './admin-lookup-data-service';
import { AdminLookupTableConfigService } from './admin-lookup-table-config-service';

@Injectable({
  providedIn: 'root',
})

export class AdminLookupTableService {

  public allowDelete: boolean = false;
  public allowEditAttributes: boolean = false;
  public allowEditValues: boolean = false;
  public selectedLookup: RunnableLookupBriefInfo;
  public scenario: ItemScenarios;
  public copyState: RunnableLookupCopyState;

  public attributes: BehaviorSubject<RunnableLookupColumn[]> = new BehaviorSubject<RunnableLookupColumn[]>([]);
  public attributeConfig: TableConfig = new TableConfig();

  public currentPageRows: BehaviorSubject<RunnableLookupRow[]> = new BehaviorSubject<RunnableLookupRow[]>([]);
  public valueConfig: TableConfig = new TableConfig();

  public showAttributes: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public showValues: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public attributeTableParameters: TableParameters = null;
  public valuesTableParameters: TableParameters = null;

  private get lookupId(): string {
    return this.selectedLookup ? this.selectedLookup.lookupId : undefined;
  }

  public get attributeIds(): string[] {
    return _.map(this.attributes.value, col => col.lookupColumnId);
  }

  public get hasAnyAttribute(): boolean {
    return this.attributes.value.length > 0;
  }

  public get hasRows(): boolean {
    const rows = this.currentPageRows.value;
    const columns = this.attributes.value;
    return columns && columns.length > 0 && rows && rows.length > 0;
  }

  public get modifiedColumns(): RunnableLookupColumn[] {
    return this.attributes.value.filter(a => a.state !== RunnableLookupItemState.Unchanged);
  }

  public get modifiedRows(): RunnableLookupRow[] {
    return this.currentPageRows.value.filter(row => row.state !== RunnableLookupItemState.Unchanged);
  }

  public get hasModifiedOrAddedRecords(): boolean {
    return this.modifiedRows.some(x => x.state !== RunnableLookupItemState.Deleted)
      || this.modifiedColumns.some(x => x.state !== RunnableLookupItemState.Deleted);
  }

  public selectedAttributes: RunnableLookupColumn[] = [];
  public selectedRows: RunnableLookupRow[] = [];
  private currentValueLoadEvent: LazyLoadEvent = this.tableConfigService.defaultValueLoadEvent;

  constructor(
    private backendService: BackendService,
    private backendPaginationService: BackendPaginationService,
    private generateGuidService: GenerateGuidService,
    private i18NextPipe: I18NextPipe,
    private dataService: AdminLookupDataService,
    private tableConfigService: AdminLookupTableConfigService
  ) { }

  public setParams(
    lookup: RunnableLookupBriefInfo,
    allowEditAttributes: boolean,
    allowEditValues: boolean,
    scenario: ItemScenarios = ItemScenarios.Existing,
    copyState: RunnableLookupCopyState = RunnableLookupCopyState.NoCopy
  ): void {
    this.selectedLookup = lookup;
    this.allowEditAttributes = allowEditAttributes;
    this.allowEditValues = allowEditValues;
    this.scenario = scenario;
    this.copyState = copyState;
    this.tableConfigService.scenario = scenario;
  }

  public loadRunnableLookupAttributes(tableParameters: TableParameters): Observable<RunnableLookupColumn[]> {
    return this.backendPaginationService
      .paginateWithParameters<RunnableLookupColumn>(RunnableLookupColumn, tableParameters, tableParameters.skip, tableParameters.take, false);
  }

  public getRunnableLookupColumnsCount(lookupId: string, tableParameters: TableParameters): Observable<number> {
    if (lookupId) {
      return this.backendPaginationService.getCountWithParameters<RunnableLookupColumn>(RunnableLookupColumn, tableParameters, false);
    } else {
      return of(0);
    }
  }

  public loadAttributesAndValues(): void {
    const event = this.tableConfigService.defaultAttributeLoadEvent;
    this.loadRunnableLookupColumnsLazy(event);
    this.unlockAttributes();
  }

  public loadRunnableLookupColumnsLazy(event: LazyLoadEvent): void {
    if (!this.lookupId) return;

    const tableParameters = this.tableConfigService.createTableParameters(event, this.lookupId, this.attributeConfig.columns);
    tableParameters.skip = 0;
    this.attributeTableParameters = tableParameters;
    this.loadRunnableLookupColumnsAndCount(tableParameters);
  }

  private loadRunnableLookupColumns(tableParameters: TableParameters): void {
    this.loadRunnableLookupAttributes(tableParameters)
      .pipe(take(1), finalize(() => this.finishAttributeLoading()))
      .subscribe(columns => {
        this.showValues.next(columns && columns.length > 0);
        this.showAttributes.next(true);
        this.assignAttributes(columns);
      });
  }

  public initializeAttributeTableConfig(): void {
    this.attributeConfig = this.tableConfigService.createAttributeTableConfig(this.allowEditAttributes);
    this.updateDeleteAttributesButton();
  }

  private updateDeleteAttributesButton(): void {
    this.tableConfigService.updateTableConfigDeleteAction(this.attributeConfig, this.allowEditAttributes, this.selectedAttributes.length > 0);
  }

  private loadRunnableLookupColumnsAndCount(tableParameters: TableParameters): void {
    this.startAttributeLoading();
    this.getRunnableLookupColumnsCount(this.lookupId, tableParameters)
      .pipe(take(1))
      .subscribe(attributesCount => {
        this.tableConfigService.setRecordCountConfigParameters(this.attributeConfig, tableParameters, attributesCount);
        if (attributesCount > 0) {
          tableParameters.take = _.max([attributesCount, this.attributeConfig.rowsPerPage]);
          this.loadRunnableLookupColumns(tableParameters);
        } else {
          this.clearTables();
          this.finishAttributeLoading();
        }
      });
  }

  public anyOtherLoadedDisplayColumn(runnableLookupColumn: RunnableLookupColumn): boolean {
    return _.some(this.attributes.value, currentColumn => currentColumn.isDisplayColumn && currentColumn.lookupColumnId !== runnableLookupColumn.lookupColumnId);
  }

  public checkForUnloadedDisplayColumns(loadedColumnIds: string[]): Observable<boolean> {
    const request: LookupColumnRequest = new LookupColumnRequest(this.lookupId, loadedColumnIds);
    return this.backendService.getBooleanResponseForObject(CONFIG.apiRoutes.runnableLookupDisplayColumnCheck, request);
  }

  public processEditAttribute(attribute: RunnableLookupColumn) {
    this.dataService.updateUnitOfMeasureName(attribute).pipe().subscribe(() => {
      if (attribute.state === RunnableLookupItemState.Unchanged) {
        attribute.state = RunnableLookupItemState.Modified;
      }
      this.updateTableForce();
      this.onAttributeChanges([attribute]);
      this.lockAttributes();
    });
  }

  private updateTableForce(): void {
    this.assignAttributes(this.attributes.value);
  }

  private assignAttributes(attributes: RunnableLookupColumn[]) {
    this.attributes.next(attributes);
    this.onAttributeChanges(attributes);
  }

  public loadMaxColumnIndex(): Observable<number> {
    return this.getMaxColumnIndex(this.lookupId)
      .pipe(
        map(maxColumnIndexFromServer => _.max([maxColumnIndexFromServer, ...this.attributes.value.map(x => x.columnIndex)])),
      );
  }

  public getMaxColumnIndex(lookupId: string): Observable<number> {
    if (lookupId) {
      return this.backendService.getAnyById(CONFIG.apiRoutes.runnableLookupColumnsMaxColumnIndex, lookupId);
    } else {
      return of(0);
    }
  }

  public addAttribute(maxColumnIndex: number, attribute: RunnableLookupColumn): void {
    attribute.lookupId = this.lookupId;
    attribute.state = RunnableLookupItemState.Added;
    const columns = this.attributes.value;
    attribute.columnIndex = maxColumnIndex + 1;
    this.dataService.updateUnitOfMeasureName(attribute).pipe().subscribe(() => {
      columns.push(attribute);
      this.assignAttributes(columns);
      this.showValues.next(true);
      this.lockAttributes();
    });
  }

  public startAttributeLoading = (): void => this.tableConfigService.startLoading(this.attributeConfig);
  public finishAttributeLoading = (): void => this.tableConfigService.finishLoading(this.attributeConfig);

  public lockAttributes = (): void => this.tableConfigService.lock(this.attributeConfig);
  public unlockAttributes = (): void => this.tableConfigService.unlock(this.attributeConfig);

  public createRunnableLookupColumn(): RunnableLookupColumn {
    return this.dataService.createRunnableLookupColumn(!this.hasAnyAttribute);
  }

  public copy(copyState: RunnableLookupCopyState = RunnableLookupCopyState.CopyWithValues): void {
    this.showAttributes.next(true);
    this.setTablesReadonly();
    if (copyState === RunnableLookupCopyState.Copy) {
      this.clearValues();
    }
    this.showValues.next(this.hasRows);
  }

  public clearTables(): void {
    this.clearAttributes();
    this.clearValues();
    this.showValues.next(false);
  }

  public clearAttributes(): void {
    this.attributes.next([]);
    this.selectedAttributes = [];
  }

  public clearValues(): void {
    this.currentPageRows.next([]);
    this.selectedRows = [];
  }

  public get attributeDeleteConfirmationMessage(): string {
    return this.i18NextPipe.transform('LookupAttributesTableDetails.DeleteMessage');
  }

  public get valuesDeleteConfirmationMessage(): string {
    return this.i18NextPipe.transform('AdminLookupsValueTable.DeleteMessage');
  }

  public get deleteConfirmationTitle(): string {
    return this.i18NextPipe.transform('AdminLookupsActions.Delete');
  }

  public selectAttributes(attributes: RunnableLookupColumn[]) {
    this.selectedAttributes = attributes;
    this.updateDeleteAttributesButton();
  }

  public selectValues(values: RunnableLookupRow[]) {
    this.selectedRows = values;
    this.updateDeleteValuesButton();
  }

  public paginationLoadRunnableLookupRows(tableParameters: TableParameters): Observable<RunnableLookupRow[]> {
    return this.backendPaginationService
      .paginateWithParameters<RunnableLookupRow>(RunnableLookupRow, tableParameters, tableParameters.skip, tableParameters.take, false);
  }

  public getRunnableLookupRowsCount(tableParameters: TableParameters): Observable<number> {
    return this.backendPaginationService
      .getCountWithParameters<RunnableLookupRow>(RunnableLookupRow, tableParameters, false);
  }

  public loadCollections(): void {
    this.prepareForLoading();
    this.loadAttributesAndValues();
  }

  private prepareForLoading(): void {
    this.currentValueLoadEvent = this.tableConfigService.defaultValueLoadEvent;
    this.initializeTableConfigs();
    this.clearTables();
  }

  public initializeTableConfigs(): void {
    this.initializeAttributeTableConfig();
    this.initializeValueTableConfig();
  }

  public onAttributeChanges(attributes: RunnableLookupColumn[]): void {
    if (!attributes || attributes.length === 0) {
      this.clearValues();
      this.showValues.next(false);
      return;
    };

    this.disableHeaderIfWeHaveNewColumns();
    this.updateUserDefinedColumns();
    if (!this.hasRows) {
      this.loadRunnableLookupRowsLazy(this.currentValueLoadEvent);
    } else {
      this.updateRunnableLookupCellsForRows();
    }
  }

  public setTablesReadonly(): void {
    this.tableConfigService.setReadonly(this.attributeConfig);
    this.tableConfigService.setReadonly(this.valueConfig);
  }

  private initializeValueTableConfig(): void {
    const config = this.tableConfigService.createValueTableConfig(this.allowEditValues);
    this.createInitialColumns(config);
    this.valueConfig = config;
    this.updateDeleteValuesButton();
  }

  private createInitialColumns(config: TableConfig): void {
    const indexCol = new TableColumn('#', this.tableConfigService.rowIndexColumnName);
    indexCol.widthPx = 60;
    indexCol.modelChangeEvent = (row: RunnableLookupRow) => this.onRowChanged(row);
    config.columns = [indexCol];
  }

  private updateRunnableLookupCellsForRows(): void {
    const rows = this.currentPageRows.value;
    for (const row of rows) {
      for (const column of this.attributes.value) {
        const cell = row.columns[column.lookupColumnId];
        if (cell == null) {
          row.columns[column.lookupColumnId] = this.dataService.createRunnableLookupCell(
            column.lookupColumnId, column.columnIndex, row.lookupRowId);
        }
      }
    }
    this.currentPageRows.next(rows);
  }

  private getValueFieldByLookupColumnId = (lookupColumnId: string) => `columns.${lookupColumnId}.value`;

  private updateUserDefinedColumns(): void {
    const columns = this.attributes.value;
    const availableColumns = columns
      .filter(x => x.state !== RunnableLookupItemState.Deleted)
      .sort(x => x.columnIndex);

    const deletedColumns = columns.filter(x => x.state === RunnableLookupItemState.Deleted);

    this.valueConfig.columns = this.valueConfig.columns
      .filter(x => deletedColumns.find(t =>
        x.valueField === this.getValueFieldByLookupColumnId(t.lookupColumnId) == null) ||
        x.valueField === this.tableConfigService.rowIndexColumnName);

    for (const runnableLookupColumn of availableColumns) {
      const valueField = this.getValueFieldByLookupColumnId(runnableLookupColumn.lookupColumnId);
      const existedColumn = this.valueConfig.columns.find(x => x.valueField === valueField);

      if (runnableLookupColumn.dataType === LookupColumnDataType.Radio) {
        continue;
      }

      if (existedColumn != null) {
        existedColumn.header = runnableLookupColumn.headerText;
        this.dataService.setColumnInputTypeAccordingDatatype(runnableLookupColumn.dataType, existedColumn);
        continue;
      }

      const userDefinedColumn = new TableColumn(runnableLookupColumn.headerText, valueField);

      userDefinedColumn.minWidthPx = 100;
      userDefinedColumn.editable = true;
      userDefinedColumn.modelChangeEvent = (row: RunnableLookupRow) => this.onRowChanged(row);

      this.dataService.setColumnInputTypeAccordingDatatype(runnableLookupColumn.dataType, userDefinedColumn);

      this.valueConfig.columns.push(userDefinedColumn);
    }
  }

  public loadRunnableLookupRowsLazy(event: LazyLoadEvent): void {
    this.currentValueLoadEvent = event;
    this.loadRunnableLookupRows();
    this.loadTotalRecordsForTableAccordingFilter();
  }

  private createValueTableParameters(): TableParameters {
    return this.tableConfigService.createTableParameters(this.currentValueLoadEvent, this.lookupId, this.valueConfig.columns);
  }

  public loadTotalRecordsForTableAccordingFilter(): void {
    if (!this.lookupId) return;

    const tableParams = this.createValueTableParameters();
    this.getRunnableLookupRowsCount(tableParams)
      .pipe(take(1))
      .subscribe(totalRecords => {
        this.tableConfigService.setRecordCountConfigParameters(this.valueConfig, tableParams, totalRecords);
      });
  }

  public loadRunnableLookupRowsCount(): Observable<number> {
    if (!this.lookupId) {
      return of(0);
    }

    const tableParams = new TableParameters();
    tableParams.selectedItemId = this.lookupId;
    tableParams.filters = [];
    return this.getRunnableLookupRowsCount(tableParams);
  }

  private loadRunnableLookupRows(): void {
    if (!this.lookupId) {
      return;
    }

    const tableParams = this.createValueTableParameters();
    this.startValueLoading();

    this.paginationLoadRunnableLookupRows(tableParams)
      .pipe(take(1), finalize(() => this.finishValueLoading()))
      .subscribe(result => {
        this.currentPageRows.next(result);
      });
  }

  public addNewRow(): void {
    this.lockValues();

    const newRow = new RunnableLookupRow();
    newRow.lookupId = this.lookupId;
    newRow.lookupRowId = this.generateGuidService.newGuid();
    newRow.state = RunnableLookupItemState.Added;

    const values = this.currentPageRows.value;

    this.attributes.value.forEach(column => {
      const cell = this.dataService.createRunnableLookupCell(column.lookupColumnId, column.columnIndex, newRow.lookupRowId);
      newRow.columns[column.lookupColumnId] = cell;
      this.dataService.updateCellWithDefaultValue(column, cell);
    });

    this.currentPageRows.next([newRow, ...values]);
  }

  private onRowChanged(row: RunnableLookupRow): void {
    if (row.state === RunnableLookupItemState.Unchanged) {
      row.state = RunnableLookupItemState.Modified;
    }

    this.lockAttributes();
    this.lockValues();
  }

  private disableHeaderIfWeHaveNewColumns(): void {
    const hasNewColumns = this.attributes.getValue().find(x => x.state === RunnableLookupItemState.Added);

    if (hasNewColumns) {
      this.lockValues();
    } else {
      this.unlockValuesIfUnchanged();
    }
  }

  public startValueLoading = (): void => this.tableConfigService.startLoading(this.valueConfig);
  public finishValueLoading = (): void => this.tableConfigService.finishLoading(this.valueConfig);
  private lockValues = (): void => this.tableConfigService.lock(this.valueConfig);
  private unlockValues = (): void => this.tableConfigService.unlock(this.valueConfig);

  public createSaveData(lookup: RunnableLookupBriefInfo = this.selectedLookup): RunnableLookupSaveData {
    const saveData: RunnableLookupSaveData = new RunnableLookupSaveData();
    saveData.briefInfo = lookup;
    saveData.briefInfo.createdClientSide = true;
    saveData.briefInfo.lookupId = this.selectedLookup.lookupId;
    saveData.columns = this.modifiedColumns;
    saveData.rows = this.modifiedRows.map(r => this.dataService.toRunnableLookupSaveDataRow(r));
    saveData.attributesTableParameters = this.attributeTableParameters;
    saveData.valuesTableParameters = this.createValueTableParameters();
    saveData.copyState = this.copyState;

    return saveData;
  }

  public deleteRunnableLookupColumns(): Observable<boolean> {
    const attributeIdsToDelete = this.selectedAttributes.map(a => a.lookupColumnId);

    return this.backendService.deleteRange<RunnableLookupColumn>(RunnableLookupColumn, attributeIdsToDelete);
  }

  public deleteRunnableLookupRows(): Observable<RunnableLookupRowsRangeDeleteData> {
    const rangeDeleteData = this.dataService.createRunnableLookupRowsRangeDeleteData(this.currentPageRows.value, this.selectedRows, this.lookupId);

    return this.backendService.postObject<RunnableLookupRowsRangeDeleteData>(RunnableLookupRowsRangeDeleteData, rangeDeleteData);
  }

  public handleDeletedColumns(): void {
    const attributeIdsToDelete = this.selectedAttributes.map(a => a.lookupColumnId);
    const attributes = this.attributes.value.filter(x => !attributeIdsToDelete.includes(x.lookupColumnId));

    this.assignAttributes(attributes);
    this.selectedAttributes = [];
    this.updateDeleteAttributesButton();
    this.unlockAttributesIfUnchanged();
  }

  private unlockAttributesIfUnchanged() {
    if (this.dataService.isUnchangedCollection(this.attributes.value)) {
      this.unlockAttributes();
    }
  }

  private updateDeleteValuesButton() {
    this.tableConfigService.updateTableConfigDeleteAction(this.valueConfig, this.allowEditValues, this.selectedRows.length > 0);
  }

  public setSelectedRowDeletedState(): void {
    this.selectedRows.forEach(x => x.state = RunnableLookupItemState.Deleted);
  }

  public handleDeletedRows(lookupRowsRangeDeleteData: RunnableLookupRowsRangeDeleteData): void {

    const values = this.currentPageRows.value;
    lookupRowsRangeDeleteData.rowsToUpdate.forEach(x => {
      const indexToUpdate = values.findIndex(t => t.lookupRowId === x.lookupRowId);

      if (indexToUpdate !== -1) {
        values[indexToUpdate].rowIndex = x.rowIndex;
        values[indexToUpdate] = _.cloneDeep(values[indexToUpdate]);
      }
    });

    const selectedRunnableLookupRowsId = this.selectedRows.map(x => x.lookupRowId);
    this.currentPageRows.next(values.filter(x => !selectedRunnableLookupRowsId.includes(x.lookupRowId)));

    this.selectedRows = [];
    this.updateDeleteValuesButton();
    this.refreshTableIfWeDontHaveRows();
    this.unlockValuesIfUnchanged();
  }

  private unlockValuesIfUnchanged(): void {
    if (this.dataService.isUnchangedCollection(this.currentPageRows.value)) {
      this.unlockValues();
    }
  }

  private refreshTableIfWeDontHaveRows(): void {
    if (this.currentPageRows.value.length === 0 && this.attributes.value.length > 0) {
      this.loadRunnableLookupRows();
      this.loadTotalRecordsForTableAccordingFilter();
    }
  }
}
